import { Anchor, Stack, Text } from '@mantine/core'
import { Link } from 'react-router-dom'
import { DataTable } from '@lighthouse/design'
import type { DataTableColumn } from '@lighthouse/design'
import { NOT_MEASURED } from '../../utils/format'
import { SignalHandlingCell, SignalSeverity, SignalStateLabel } from './SignalBits'
import { evidenceText, signalLinks, signalTitle, sinceText } from './signalFormat'
import type { Signal } from '../../types/signals'

/**
 * The Inbox's table: Severity · Signal · State · Handling · Since · Last observed · a link to the subject.
 *
 * Handling says who has each signal (D6b) — "Unowned", "Assigned to Bob · not acknowledged", "Acknowledged by Alice ·
 * 5m ago" — beside State, never instead of it: an acknowledged signal is still open. Where the handling table does
 * not exist the cell is a dash saying so, never "Unowned", because unknown is not unowned. There is still no action
 * column: acting on a signal happens in the drawer, behind the handling gate. The title opens the drawer
 * (`?signal=`) as a real link; the last column goes to the page that owns the subject, because the store keeps no
 * copy of it.
 */
export function SignalTable({ signals, detailHref, loading = false }: {
  signals: Signal[]
  /** The Inbox URL with `signal={id}` set, the rest of its filters kept. */
  detailHref: (id: string) => string
  loading?: boolean
}) {
  const columns: DataTableColumn<Signal>[] = [
    {
      key: 'severity',
      header: 'Severity',
      width: 120,
      render: (s) => <SignalSeverity severity={s.severity} />,
    },
    {
      key: 'signal',
      header: 'Signal',
      render: (s) => (
        <Stack gap={0}>
          <Anchor component={Link} to={detailHref(s.id)} size="sm" fw={500}>{signalTitle(s)}</Anchor>
          <Text size="xs" c="dimmed" style={{ overflowWrap: 'anywhere' }}>{s.subjectLabel || NOT_MEASURED}</Text>
        </Stack>
      ),
    },
    {
      key: 'state',
      header: 'State',
      width: 220,
      render: (s) => <SignalStateLabel signal={s} />,
    },
    {
      key: 'handling',
      header: 'Handling',
      width: 220,
      render: (s) => <SignalHandlingCell signal={s} />,
    },
    {
      key: 'since',
      header: 'Since',
      width: 170,
      render: (s) => <Text size="sm">{sinceText(s)}</Text>,
    },
    {
      key: 'observed',
      header: 'Last observed',
      width: 170,
      render: (s) => {
        const evidence = evidenceText(s)
        return (
          <Stack gap={0}>
            <Text size="sm" c={evidence.observed ? undefined : 'dimmed'}>{evidence.observed ?? NOT_MEASURED}</Text>
            <Text size="xs" c="dimmed">{evidence.checked}</Text>
          </Stack>
        )
      },
    },
    {
      key: 'link',
      header: 'Subject',
      width: 170,
      render: (s) => {
        const links = signalLinks(s)
        if (links.length === 0) return <Text size="sm" c="dimmed">{NOT_MEASURED}</Text>
        return (
          <Stack gap={0}>
            {links.map((link) => (
              <Anchor key={link.to} component={Link} to={link.to} size="xs">{link.label}</Anchor>
            ))}
          </Stack>
        )
      },
    },
  ]

  return <DataTable columns={columns} rows={signals} rowKey={(s) => s.id} loading={loading} />
}
